import React, { useState, useEffect, useMemo } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import axios from "axios";
import {
  Users, Plus, Trash2, Edit, X, Phone, Mail, Wrench, DollarSign,
  MapPin, Clock, Shield, User, Hash, Home, AlertCircle,
  Briefcase, CheckCircle, Calendar, ChevronRight, ArrowLeft
} from "lucide-react";

const ROLES = ["technician", "manager"];
const SPECIALTIES = ["Drywall", "Painting", "Flooring", "Tile", "Roofing", "Cabinets", "Electrical", "Doors", "Windows", "Stucco", "Plumbing", "General"];

const ROLE_BADGE = {
  manager: "bg-purple-500/20 text-purple-400 border-purple-500/40",
  technician: "bg-[#FF6B35]/20 text-[#FF6B35] border-[#FF6B35]/40"
};

const JOB_STATUS_COLORS = {
  assigned: "bg-blue-500/20 text-blue-400",
  accepted: "bg-green-500/20 text-green-400",
  needs_review: "bg-yellow-500/20 text-yellow-400",
  approved: "bg-emerald-500/20 text-emerald-400",
  in_progress: "bg-orange-500/20 text-orange-400",
  completed: "bg-green-600/20 text-green-300",
  cancelled: "bg-red-500/20 text-red-400",
  scheduled: "bg-blue-500/20 text-blue-400",
  on_hold: "bg-gray-500/20 text-gray-400"
};

const JOB_STATUS_LABELS = {
  assigned: "Assigned", accepted: "Accepted", needs_review: "Needs Review",
  approved: "Approved", in_progress: "In Progress", completed: "Completed",
  cancelled: "Cancelled", scheduled: "Scheduled", on_hold: "On Hold"
};

const EmployeesTab = ({ onRefresh }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileTab, setProfileTab] = useState("info");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", role: "technician",
    specialties: [], hourly_rate: "", notes: "", password: "",
    address: "", emergency_contact_name: "", emergency_contact_phone: ""
  });

  useEffect(() => { fetchEmployees(); fetchCheckins(); }, []);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`, { withCredentials: true });
      setEmployees(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchCheckins = async () => {
    try {
      const res = await axios.get(`${API}/employee/checkins`, { withCredentials: true });
      setCheckins(res.data);
    } catch (e) { /* ignore */ }
  };

  const resetForm = () => {
    setForm({
      name: "", email: "", phone: "", role: "technician",
      specialties: [], hourly_rate: "", notes: "", password: "",
      address: "", emergency_contact_name: "", emergency_contact_phone: ""
    });
    setEditingId(null);
  };

  const openEdit = (emp) => {
    setForm({
      name: emp.name, email: emp.email || "", phone: emp.phone,
      role: emp.role || "technician", specialties: emp.specialties || [],
      hourly_rate: emp.hourly_rate?.toString() || "", notes: emp.notes || "",
      address: emp.address || "",
      emergency_contact_name: emp.emergency_contact_name || "",
      emergency_contact_phone: emp.emergency_contact_phone || ""
    });
    setEditingId(emp.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    const payload = { ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 };
    try {
      if (editingId) {
        const { password, ...updatePayload } = payload;
        await axios.patch(`${API}/employees/${editingId}`, updatePayload, { withCredentials: true });
        toast.success("Employee updated");
      } else {
        if (!form.email || !form.password) { toast.error("Email and password required for new employees (for portal login)"); return; }
        await axios.post(`${API}/employees/register`, payload, { withCredentials: true });
        toast.success("Employee added with portal access");
      }
      setShowModal(false);
      resetForm();
      fetchEmployees();
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save employee"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this employee?")) return;
    try {
      await axios.delete(`${API}/employees/${id}`, { withCredentials: true });
      toast.success("Employee removed");
      fetchEmployees();
      if (onRefresh) onRefresh();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const toggleSpecialty = (spec) => {
    setForm(p => ({
      ...p,
      specialties: p.specialties.includes(spec) ? p.specialties.filter(s => s !== spec) : [...p.specialties, spec]
    }));
  };

  const openProfile = async (empId) => {
    setProfileLoading(true);
    setProfileTab("info");
    try {
      const res = await axios.get(`${API}/employees/${empId}/profile`, { withCredentials: true });
      setProfileData(res.data);
    } catch (e) {
      toast.error("Failed to load employee profile");
    } finally { setProfileLoading(false); }
  };

  const closeProfile = () => { setProfileData(null); };

  return (
    <div className="space-y-4" data-testid="employees-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Team Members ({employees.length})</h2>
        <button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-employee-btn"
          className="flex items-center space-x-2 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold">
          <Plus size={16} /><span>Add Employee</span>
        </button>
      </div>

      {/* Location Tracking */}
      {checkins.length > 0 && (
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center space-x-2">
            <MapPin size={14} className="text-[#FF6B35]" /><span>Team Locations</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {checkins.map(c => (
              <div key={c.id} className="bg-[#1F2027] rounded-lg p-3" data-testid={`location-${c.id}`}>
                <p className="text-white text-sm font-medium">{c.name}</p>
                <p className="text-[#A0AAB2] text-xs flex items-center space-x-1 mt-1">
                  <MapPin size={10} /><span>{c.last_location || "Not checked in"}</span>
                </p>
                {c.last_checkin && (
                  <p className="text-[#A0AAB2] text-[10px] flex items-center space-x-1 mt-0.5">
                    <Clock size={10} /><span>{new Date(c.last_checkin).toLocaleString()}</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Employee List */}
      <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
          </div>
        ) : employees.length === 0 ? (
          <p className="text-[#A0AAB2] text-center py-8">No team members yet. Add your first employee above.</p>
        ) : (
          <div className="space-y-3">
            {employees.map(emp => (
              <div key={emp.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4" data-testid={`employee-row-${emp.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 cursor-pointer" onClick={() => openProfile(emp.id)} data-testid={`employee-name-click-${emp.id}`}>
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-white font-semibold hover:text-[#FF6B35] transition-colors">{emp.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${ROLE_BADGE[emp.role] || ROLE_BADGE.technician}`}>
                        {emp.role === "manager" ? "Manager" : "Technician"}
                      </span>
                      {emp.employee_number && (
                        <span className="text-xs text-[#A0AAB2] font-mono">{emp.employee_number}</span>
                      )}
                      {!emp.active && <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full">Inactive</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-[#A0AAB2]">
                      <span className="flex items-center space-x-1"><Phone size={12} /><span>{emp.phone}</span></span>
                      {emp.email && <span className="flex items-center space-x-1"><Mail size={12} /><span>{emp.email}</span></span>}
                      {emp.hourly_rate > 0 && <span className="flex items-center space-x-1"><DollarSign size={12} /><span>${emp.hourly_rate}/hr</span></span>}
                    </div>
                    {emp.specialties?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {emp.specialties.map(s => (
                          <span key={s} className="text-[10px] px-2 py-0.5 bg-[#2A2B32] text-[#A0AAB2] rounded-full">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button onClick={(e) => { e.stopPropagation(); openProfile(emp.id); }}
                      className="p-2 bg-[#FF6B35]/20 text-[#FF6B35] rounded-lg hover:bg-[#FF6B35]/30" data-testid={`view-profile-${emp.id}`}>
                      <User size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); openEdit(emp); }} className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30" data-testid={`edit-employee-${emp.id}`}>
                      <Edit size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(emp.id); }} className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30" data-testid={`delete-employee-${emp.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && <EmployeeFormModal
        form={form} setForm={setForm} editingId={editingId}
        onSave={handleSave} onClose={() => { setShowModal(false); resetForm(); }}
        toggleSpecialty={toggleSpecialty}
      />}

      {/* Profile Modal */}
      {(profileData || profileLoading) && <EmployeeProfileModal
        data={profileData} loading={profileLoading}
        activeTab={profileTab} setActiveTab={setProfileTab}
        onClose={closeProfile} onEdit={(emp) => { closeProfile(); openEdit(emp); }}
      />}
    </div>
  );
};

/* ========== EMPLOYEE FORM MODAL ========== */
const EmployeeFormModal = ({ form, setForm, editingId, onSave, onClose, toggleSpecialty }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="employee-modal">
    <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-lg p-6 my-8 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white">{editingId ? "Edit Employee" : "Add Employee"}</h3>
        <button onClick={onClose} className="p-2 text-[#A0AAB2] hover:text-white"><X size={20} /></button>
      </div>
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="text-[#A0AAB2] text-sm mb-1 block">Full Name *</label>
          <input type="text" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}
            className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="Full name" data-testid="emp-name" />
        </div>
        {/* Phone */}
        <div>
          <label className="text-[#A0AAB2] text-sm mb-1 block">Phone *</label>
          <input type="tel" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))}
            className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="(555) 123-4567" data-testid="emp-phone" />
        </div>
        {/* Email */}
        <div>
          <label className="text-[#A0AAB2] text-sm mb-1 block">Email {!editingId && "*"}</label>
          <input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))}
            className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="email@example.com" data-testid="emp-email" />
        </div>
        {/* Password (new only) */}
        {!editingId && (
          <div>
            <label className="text-[#A0AAB2] text-sm mb-1 block">Portal Password *</label>
            <input type="text" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))}
              className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="Login password for team portal" data-testid="emp-password" />
          </div>
        )}
        {/* Role */}
        <div>
          <label className="text-[#A0AAB2] text-sm mb-1 block">Role *</label>
          <div className="grid grid-cols-2 gap-3">
            {ROLES.map(r => (
              <button key={r} type="button" onClick={() => setForm(p => ({...p, role: r}))}
                data-testid={`emp-role-${r}`}
                className={`px-4 py-3 rounded-lg text-sm font-medium border transition-all flex items-center justify-center space-x-2 ${
                  form.role === r ? (r === "manager" ? "bg-purple-500/20 text-purple-400 border-purple-500/50" : "bg-[#FF6B35]/20 text-[#FF6B35] border-[#FF6B35]/50")
                  : "bg-[#1F2027] text-[#A0AAB2] border-[#2A2B32]"
                }`}>
                {r === "manager" ? <Shield size={16} /> : <Wrench size={16} />}
                <span className="capitalize">{r}</span>
              </button>
            ))}
          </div>
        </div>
        {/* Address */}
        <div>
          <label className="text-[#A0AAB2] text-sm mb-1 block">Full Address</label>
          <input type="text" value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))}
            className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="123 Main St, City, FL 33801" data-testid="emp-address" />
        </div>
        {/* Emergency Contact */}
        <div className="bg-[#1F2027] rounded-lg p-4 border border-[#2A2B32]">
          <h4 className="text-white text-sm font-semibold mb-3 flex items-center space-x-2">
            <AlertCircle size={14} className="text-red-400" /><span>Emergency Contact</span>
          </h4>
          <div className="space-y-3">
            <input type="text" value={form.emergency_contact_name} onChange={e => setForm(p => ({...p, emergency_contact_name: e.target.value}))}
              className="w-full bg-[#15161C] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="Contact name" data-testid="emp-emergency-name" />
            <input type="tel" value={form.emergency_contact_phone} onChange={e => setForm(p => ({...p, emergency_contact_phone: e.target.value}))}
              className="w-full bg-[#15161C] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="Contact phone" data-testid="emp-emergency-phone" />
          </div>
        </div>
        {/* Hourly Rate */}
        <div>
          <label className="text-[#A0AAB2] text-sm mb-1 block">Hourly Rate ($)</label>
          <input type="number" value={form.hourly_rate} onChange={e => setForm(p => ({...p, hourly_rate: e.target.value}))}
            className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="0" data-testid="emp-rate" />
        </div>
        {/* Specialties */}
        <div>
          <label className="text-[#A0AAB2] text-sm mb-1 block">Specialties</label>
          <div className="flex flex-wrap gap-2">
            {SPECIALTIES.map(s => (
              <button key={s} type="button" onClick={() => toggleSpecialty(s)}
                className={`px-3 py-1 rounded-lg text-xs ${form.specialties.includes(s) ? "bg-[#FF6B35]/20 text-[#FF6B35] border border-[#FF6B35]/50" : "bg-[#2A2B32] text-[#A0AAB2]"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        {/* Notes */}
        <div>
          <label className="text-[#A0AAB2] text-sm mb-1 block">Notes</label>
          <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2}
            className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white resize-none" data-testid="emp-notes" />
        </div>
      </div>
      <div className="flex space-x-3 mt-6">
        <button onClick={onClose} className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg">Cancel</button>
        <button onClick={onSave} className="flex-1 py-3 gradient-btn rounded-lg font-semibold" data-testid="save-employee-btn">
          {editingId ? "Update" : "Add Employee"}
        </button>
      </div>
    </div>
  </div>
);

/* ========== EMPLOYEE PROFILE MODAL ========== */
const EmployeeProfileModal = ({ data, loading, activeTab, setActiveTab, onClose, onEdit }) => {
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }
  if (!data) return null;

  const emp = data.employee;
  const tabs = [
    { id: "info", label: "Profile", icon: User },
    { id: "jobs", label: "Jobs", icon: Briefcase },
    { id: "earnings", label: "Earnings", icon: DollarSign }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="employee-profile-modal">
      <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-2xl p-6 my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${emp.role === "manager" ? "bg-purple-500/20 text-purple-400" : "bg-[#FF6B35]/20 text-[#FF6B35]"}`}>
              {emp.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{emp.name}</h3>
              <div className="flex items-center space-x-2 mt-0.5">
                <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${ROLE_BADGE[emp.role] || ROLE_BADGE.technician}`}>
                  {emp.role === "manager" ? "Manager" : "Technician"}
                </span>
                {emp.employee_number && <span className="text-xs text-[#A0AAB2] font-mono" data-testid="profile-emp-number">{emp.employee_number}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={() => onEdit(emp)} className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30" data-testid="profile-edit-btn">
              <Edit size={16} />
            </button>
            <button onClick={onClose} className="p-2 text-[#A0AAB2] hover:text-white" data-testid="close-profile-btn">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tab Nav */}
        <div className="flex space-x-1 bg-[#1F2027] rounded-lg p-1 mb-6">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`profile-tab-${t.id}`}
              className={`flex-1 flex items-center justify-center space-x-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                activeTab === t.id ? "bg-[#FF6B35] text-white" : "text-[#A0AAB2] hover:text-white"
              }`}>
              <t.icon size={14} /><span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "info" && <ProfileInfoTab emp={emp} />}
        {activeTab === "jobs" && <ProfileJobsTab data={data} />}
        {activeTab === "earnings" && <ProfileEarningsTab data={data} />}
      </div>
    </div>
  );
};

/* ----- Profile: Info Tab ----- */
const ProfileInfoTab = ({ emp }) => (
  <div className="space-y-4" data-testid="profile-info-tab">
    {/* Contact Info */}
    <div className="bg-[#1F2027] rounded-lg p-4">
      <h4 className="text-white font-semibold text-sm mb-3">Contact Information</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InfoRow icon={Phone} label="Phone" value={emp.phone} testId="profile-phone" />
        <InfoRow icon={Mail} label="Email" value={emp.email} testId="profile-email" />
        <InfoRow icon={Home} label="Address" value={emp.address || "Not provided"} testId="profile-address" span />
      </div>
    </div>
    {/* Role & ID */}
    <div className="bg-[#1F2027] rounded-lg p-4">
      <h4 className="text-white font-semibold text-sm mb-3">Employment Details</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InfoRow icon={Hash} label="Employee #" value={emp.employee_number || "N/A"} testId="profile-emp-id" />
        <InfoRow icon={Shield} label="Role" value={emp.role === "manager" ? "Manager" : "Technician"} testId="profile-role" />
        <InfoRow icon={DollarSign} label="Hourly Rate" value={emp.hourly_rate > 0 ? `$${emp.hourly_rate}/hr` : "Not set"} testId="profile-rate" />
        <InfoRow icon={Calendar} label="Joined" value={emp.created_at ? new Date(emp.created_at).toLocaleDateString() : "N/A"} testId="profile-joined" />
      </div>
    </div>
    {/* Emergency Contact */}
    <div className="bg-[#1F2027] rounded-lg p-4 border border-red-500/20">
      <h4 className="text-white font-semibold text-sm mb-3 flex items-center space-x-2">
        <AlertCircle size={14} className="text-red-400" /><span>Emergency Contact</span>
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InfoRow icon={User} label="Name" value={emp.emergency_contact_name || "Not provided"} testId="profile-ec-name" />
        <InfoRow icon={Phone} label="Phone" value={emp.emergency_contact_phone || "Not provided"} testId="profile-ec-phone" />
      </div>
    </div>
    {/* Specialties */}
    {emp.specialties?.length > 0 && (
      <div className="bg-[#1F2027] rounded-lg p-4">
        <h4 className="text-white font-semibold text-sm mb-3">Specialties</h4>
        <div className="flex flex-wrap gap-2">
          {emp.specialties.map(s => (
            <span key={s} className="px-3 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] rounded-lg text-xs font-medium border border-[#FF6B35]/20">{s}</span>
          ))}
        </div>
      </div>
    )}
    {/* Notes */}
    {emp.notes && (
      <div className="bg-[#1F2027] rounded-lg p-4">
        <h4 className="text-white font-semibold text-sm mb-2">Notes</h4>
        <p className="text-[#A0AAB2] text-sm whitespace-pre-wrap">{emp.notes}</p>
      </div>
    )}
  </div>
);

const InfoRow = ({ icon: Icon, label, value, testId, span }) => (
  <div className={span ? "md:col-span-2" : ""} data-testid={testId}>
    <span className="text-[#A0AAB2] text-xs flex items-center space-x-1 mb-0.5">
      <Icon size={11} /><span>{label}</span>
    </span>
    <p className="text-white text-sm font-medium">{value}</p>
  </div>
);

/* ----- Profile: Jobs Tab ----- */
const ProfileJobsTab = ({ data }) => {
  const { assigned_jobs, completed_jobs, total_jobs } = data;
  return (
    <div className="space-y-4" data-testid="profile-jobs-tab">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#1F2027] rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-[#FF6B35]" data-testid="profile-total-jobs">{total_jobs}</p>
          <p className="text-[#A0AAB2] text-xs">Total Jobs</p>
        </div>
        <div className="bg-[#1F2027] rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-400" data-testid="profile-assigned-count">{assigned_jobs.length}</p>
          <p className="text-[#A0AAB2] text-xs">Active</p>
        </div>
        <div className="bg-[#1F2027] rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-green-400" data-testid="profile-completed-count">{completed_jobs.length}</p>
          <p className="text-[#A0AAB2] text-xs">Completed</p>
        </div>
      </div>

      {/* Active Jobs */}
      <div>
        <h4 className="text-white font-semibold text-sm mb-2">Active Jobs ({assigned_jobs.length})</h4>
        {assigned_jobs.length === 0 ? (
          <p className="text-[#A0AAB2] text-sm bg-[#1F2027] rounded-lg p-4">No active jobs</p>
        ) : (
          <div className="space-y-2">
            {assigned_jobs.map(j => <JobRow key={j.id} job={j} />)}
          </div>
        )}
      </div>

      {/* Completed Jobs */}
      <div>
        <h4 className="text-white font-semibold text-sm mb-2">Completed Jobs ({completed_jobs.length})</h4>
        {completed_jobs.length === 0 ? (
          <p className="text-[#A0AAB2] text-sm bg-[#1F2027] rounded-lg p-4">No completed jobs yet</p>
        ) : (
          <div className="space-y-2">
            {completed_jobs.map(j => <JobRow key={j.id} job={j} />)}
          </div>
        )}
      </div>
    </div>
  );
};

const JobRow = ({ job }) => (
  <div className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-3 flex items-center justify-between" data-testid={`profile-job-${job.id}`}>
    <div className="flex-1">
      <div className="flex items-center space-x-2 mb-1">
        <span className="text-white text-sm font-medium">{job.title}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${JOB_STATUS_COLORS[job.status] || "bg-gray-500/20 text-gray-400"}`}>
          {JOB_STATUS_LABELS[job.status] || job.status}
        </span>
      </div>
      <div className="flex items-center space-x-3 text-[#A0AAB2] text-xs">
        {job.client_name && <span>{job.client_name}</span>}
        {job.scheduled_date && (
          <span className="flex items-center space-x-1"><Calendar size={10} /><span>{job.scheduled_date}</span></span>
        )}
        {job.completed_at && (
          <span className="flex items-center space-x-1"><CheckCircle size={10} className="text-green-400" /><span>{new Date(job.completed_at).toLocaleDateString()}</span></span>
        )}
      </div>
    </div>
    {job.total_price > 0 && (
      <span className="text-[#FF6B35] text-sm font-semibold">${job.total_price.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
    )}
  </div>
);

/* ----- Profile: Earnings Tab ----- */
const ProfileEarningsTab = ({ data }) => {
  const { earnings } = data;
  return (
    <div className="space-y-4" data-testid="profile-earnings-tab">
      {/* Aggregates */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#1F2027] rounded-lg p-4 text-center">
          <p className="text-[#A0AAB2] text-xs mb-1">All Time</p>
          <p className="text-2xl font-bold text-green-400" data-testid="earnings-total">${earnings.total.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-[#1F2027] rounded-lg p-4 text-center">
          <p className="text-[#A0AAB2] text-xs mb-1">This Month</p>
          <p className="text-2xl font-bold text-blue-400" data-testid="earnings-monthly">${earnings.monthly.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-[#1F2027] rounded-lg p-4 text-center">
          <p className="text-[#A0AAB2] text-xs mb-1">This Week</p>
          <p className="text-2xl font-bold text-[#FF6B35]" data-testid="earnings-weekly">${earnings.weekly.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
      </div>

      {/* Per-Job Earnings */}
      <div>
        <h4 className="text-white font-semibold text-sm mb-2">Per-Job Earnings</h4>
        {earnings.per_job.length === 0 ? (
          <p className="text-[#A0AAB2] text-sm bg-[#1F2027] rounded-lg p-4">No job earnings recorded yet</p>
        ) : (
          <div className="space-y-2">
            {earnings.per_job.map(j => (
              <div key={j.job_id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-3 flex items-center justify-between" data-testid={`earnings-job-${j.job_id}`}>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-0.5">
                    <span className="text-white text-sm font-medium">{j.job_title}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${JOB_STATUS_COLORS[j.status] || "bg-gray-500/20 text-gray-400"}`}>
                      {JOB_STATUS_LABELS[j.status] || j.status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-[#A0AAB2] text-xs">
                    {j.client_name && <span>{j.client_name}</span>}
                    {j.scheduled_date && <span>{j.scheduled_date}</span>}
                  </div>
                </div>
                <span className={`text-sm font-bold ${j.payout > 0 ? "text-green-400" : "text-[#A0AAB2]"}`}>
                  {j.payout > 0 ? `$${j.payout.toLocaleString(undefined, {minimumFractionDigits: 2})}` : "Pending"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeesTab;
